import { cn } from '@/lib/cn';
import type { BlockPreset } from '@/components/builder/drag-data';
import { WRAPPER_PRESETS } from '@/components/builder/palette-presets';
import { docStats, findNode, isContainer, nodeLabel, type AuthoringDoc } from '@personalizehq/editor';
import { MAX_LAYOUT_DEPTH, MAX_LAYOUT_NODES } from '@personalizehq/schemas';
import type { AuthoringBlockNode as BlockNode } from '@personalizehq/schemas';
import { ArrowDown, ArrowUp, ClipboardPaste, Copy, Layers3, MoreHorizontal, Plus, Scissors, Trash2, type LucideIcon } from 'lucide-react';
import { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';

export interface NodeActionHandlers {
  onInsert: (id: string) => void;
  onReorder: (id: string, delta: -1 | 1) => void;
  onDuplicate: (id: string) => void;
  onWrap: (id: string, preset: BlockPreset) => void;
  onDelete: (id: string) => void;
  onCopy?: (id: string) => void;
  onCut?: (id: string) => void;
  onPaste?: (id: string) => void;
}

export interface NodeAction {
  id: 'insert' | 'move-up' | 'move-down' | 'duplicate' | 'copy' | 'cut' | 'paste' | 'delete';
  label: string;
  shortLabel: string;
  icon: LucideIcon;
  disabled: boolean;
  destructive?: boolean;
  run: () => void;
}

/** One eligibility model for the layer menu, canvas menu, and selection toolbar. The callbacks still
 * enforce their own tree invariants; hiding/disabling here is discoverability, never authorization. */
export function nodeActions(
  root: BlockNode,
  id: string,
  handlers: NodeActionHandlers,
  labels: AuthoringDoc['labels'] = {}
): NodeAction[] {
  const hit = findNode(root, id);
  if (!hit) return [];

  const rootNode = hit.parent === null;
  const siblings = hit.parent && isContainer(hit.parent) ? hit.parent.children : [];
  const label = nodeLabel(hit.node, labels);
  const insertRelationship = isContainer(hit.node) ? 'inside' : 'after';

  return [
    {
      id: 'insert',
      label: `Insert ${insertRelationship} ${label}`,
      shortLabel: `Insert ${insertRelationship}`,
      icon: Plus,
      disabled: false,
      run: () => handlers.onInsert(id),
    },
    {
      id: 'move-up',
      label: `Move ${label} earlier`,
      shortLabel: 'Move earlier',
      icon: ArrowUp,
      disabled: rootNode || hit.index <= 0,
      run: () => handlers.onReorder(id, -1),
    },
    {
      id: 'move-down',
      label: `Move ${label} later`,
      shortLabel: 'Move later',
      icon: ArrowDown,
      disabled: rootNode || hit.index >= siblings.length - 1,
      run: () => handlers.onReorder(id, 1),
    },
    {
      id: 'duplicate',
      label: `Duplicate ${label}`,
      shortLabel: 'Duplicate',
      icon: Copy,
      disabled: rootNode,
      run: () => handlers.onDuplicate(id),
    },
    ...(handlers.onCopy ? [{
      id: 'copy' as const,
      label: `Copy ${label}`,
      shortLabel: 'Copy',
      icon: Copy,
      disabled: rootNode,
      run: () => handlers.onCopy?.(id),
    }] : []),
    ...(handlers.onCut ? [{
      id: 'cut' as const,
      label: `Cut ${label}`,
      shortLabel: 'Cut',
      icon: Scissors,
      disabled: rootNode,
      run: () => handlers.onCut?.(id),
    }] : []),
    ...(handlers.onPaste ? [{
      id: 'paste' as const,
      label: `Paste near ${label}`,
      shortLabel: 'Paste',
      icon: ClipboardPaste,
      disabled: false,
      run: () => handlers.onPaste?.(id),
    }] : []),
    {
      id: 'delete',
      label: `Delete ${label}`,
      shortLabel: 'Delete',
      icon: Trash2,
      disabled: rootNode,
      destructive: true,
      run: () => handlers.onDelete(id),
    },
  ];
}

function nodeLevel(root: BlockNode, id: string, level = 1): number | undefined {
  if (root.id === id) return level;
  if (!isContainer(root)) return undefined;
  for (const child of root.children) {
    const found = nodeLevel(child, id, level + 1);
    if (found !== undefined) return found;
  }
  return undefined;
}

/** Wrapping adds exactly one node and one level above the selected subtree. Check both document
 * bounds before presenting the action; the handler repeats this check before mutation. */
export function canWrapNode(root: BlockNode, id: string): boolean {
  const hit = findNode(root, id);
  const level = nodeLevel(root, id);
  if (!hit?.parent || level === undefined) return false;
  return docStats(root).nodes < MAX_LAYOUT_NODES && level + docStats(hit.node).depth <= MAX_LAYOUT_DEPTH;
}

export interface ContextMenuState {
  id: string;
  x: number;
  y: number;
}

/** Small point-anchored menu shared by the canvas and layers. It portals out of clipped rails and
 * restores focus to the invoking element after dismissal. */
export function NodeContextMenu({
  state,
  root,
  labels,
  handlers,
  onClose,
  onRename,
}: {
  state: ContextMenuState | null;
  root: BlockNode;
  labels: AuthoringDoc['labels'];
  handlers: NodeActionHandlers;
  onClose: () => void;
  onRename?: ((id: string) => void) | undefined;
}): React.JSX.Element | null {
  const menuRef = useRef<HTMLDivElement>(null);
  const restoreRef = useRef<HTMLElement | null>(null);
  const onCloseRef = useRef(onClose);

  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  useEffect(() => {
    if (!state) return;
    restoreRef.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const firstItem = menuRef.current?.querySelector<HTMLButtonElement>('button:not(:disabled)');
    (firstItem ?? menuRef.current)?.focus();

    const dismiss = (event: PointerEvent): void => {
      if (event.target instanceof Node && menuRef.current?.contains(event.target)) return;
      onCloseRef.current();
    };
    window.addEventListener('pointerdown', dismiss, true);
    return () => {
      window.removeEventListener('pointerdown', dismiss, true);
      restoreRef.current?.focus();
    };
  }, [state]);

  if (!state) return null;
  const hit = findNode(root, state.id);
  if (!hit) return null;
  const actions = nodeActions(root, state.id, handlers, labels).filter((action) => !action.disabled);
  const label = nodeLabel(hit.node, labels);
  const canRename = Boolean(onRename && hit.parent);
  const canWrap = canWrapNode(root, state.id);

  return createPortal(
    <div
      ref={menuRef}
      role="menu"
      aria-label={`${label} actions`}
      tabIndex={-1}
      className="fixed z-[100] max-h-[calc(100vh-16px)] min-w-48 overflow-y-auto rounded-md border bg-popover p-1 text-popover-foreground shadow-xl outline-none"
      style={{ left: Math.min(state.x, window.innerWidth - 216), top: Math.max(8, Math.min(state.y, window.innerHeight - 420)) }}
      onKeyDown={(event) => {
        if (event.key === 'Escape') {
          event.preventDefault();
          onClose();
          return;
        }
        if (!['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) return;
        const items = [...event.currentTarget.querySelectorAll<HTMLButtonElement>('button:not(:disabled)')];
        if (items.length === 0) return;
        event.preventDefault();
        const current = items.indexOf(document.activeElement as HTMLButtonElement);
        const next =
          event.key === 'Home'
            ? 0
            : event.key === 'End'
              ? items.length - 1
              : event.key === 'ArrowDown'
                ? (current + 1 + items.length) % items.length
                : (current - 1 + items.length) % items.length;
        items[next]?.focus();
      }}
    >
      <p className="max-w-52 truncate px-2 py-1 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
        {label}
      </p>
      {canRename ? (
        <MenuButton
          label={`Rename ${label}`}
          onClick={() => {
            onRename?.(state.id);
            onClose();
          }}
        >
          Rename
        </MenuButton>
      ) : null}
      {canWrap ? (
        <div role="group" aria-label="Wrap with" className="my-1 border-y py-1">
          <p className="px-2 py-1 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
            Wrap with
          </p>
          {WRAPPER_PRESETS.map((preset) => (
            <MenuButton
              key={preset.label}
              label={`Wrap ${label} with ${preset.label}`}
              onClick={() => {
                handlers.onWrap(state.id, preset);
                onClose();
              }}
            >
              <Layers3 className="size-3.5" />
              {preset.label}
            </MenuButton>
          ))}
        </div>
      ) : null}
      {actions.map((action) => {
        const Icon = action.icon;
        return (
          <MenuButton
            key={action.id}
            label={action.label}
            disabled={action.disabled}
            {...(action.destructive === undefined ? {} : { destructive: action.destructive })}
            onClick={() => {
              action.run();
              onClose();
            }}
          >
            <Icon className="size-3.5" />
            {action.shortLabel}
          </MenuButton>
        );
      })}
    </div>,
    document.body
  );
}

function MenuButton({
  label,
  disabled,
  destructive,
  onClick,
  children,
}: {
  label: string;
  disabled?: boolean;
  destructive?: boolean;
  onClick: () => void;
  children: React.ReactNode;
}): React.JSX.Element {
  return (
    <button
      type="button"
      role="menuitem"
      aria-label={label}
      disabled={disabled}
      className={cn(
        'flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-left text-xs outline-none hover:bg-accent focus:bg-accent disabled:pointer-events-none disabled:opacity-40',
        destructive && 'text-destructive'
      )}
      onClick={onClick}
    >
      {children}
    </button>
  );
}

export function NodeSelectionToolbar({
  root,
  id,
  handlers,
  labels = {},
  style,
  onMore,
}: {
  root: BlockNode;
  id: string;
  handlers: NodeActionHandlers;
  labels?: AuthoringDoc['labels'] | undefined;
  style: React.CSSProperties;
  onMore: (anchor: DOMRect) => void;
}): React.JSX.Element | null {
  const actions = nodeActions(root, id, handlers, labels);
  if (actions.length === 0) return null;
  const primary = actions.filter(
    (action) => !action.disabled && (action.id === 'insert' || action.id === 'duplicate')
  );
  const secondary = actions.filter(
    (action) => !action.disabled && action.id !== 'insert' && action.id !== 'duplicate'
  );

  return (
    <div
      role="toolbar"
      aria-label="Selected block actions"
      className="pointer-events-auto absolute z-10 flex items-center gap-0.5 rounded-md border bg-background/95 p-0.5 text-foreground shadow-lg backdrop-blur"
      style={style}
    >
      {primary.map((action) => {
        const Icon = action.icon;
        return (
          <button
            key={action.id}
            type="button"
            aria-label={action.label}
            title={action.shortLabel}
            disabled={action.disabled}
            className={cn(
              'grid size-7 place-items-center rounded-sm hover:bg-secondary focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:opacity-30',
              action.destructive && 'text-destructive'
            )}
            onClick={(event) => {
              event.stopPropagation();
              action.run();
            }}
          >
            <Icon className="size-3.5" />
          </button>
        );
      })}
      {secondary.length > 0 ? (
        <button
          type="button"
          aria-label={`More actions for ${nodeLabel(findNode(root, id)!.node, labels)}`}
          title="More actions"
          className="grid size-8 place-items-center rounded-sm hover:bg-secondary focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
          onClick={(event) => {
            event.stopPropagation();
            onMore(event.currentTarget.getBoundingClientRect());
          }}
        >
          <MoreHorizontal className="size-3.5" />
        </button>
      ) : null}
    </div>
  );
}
